import { z } from 'zod';

/**
 * FHIR MedicationRequest-aligned schemas for medication ordering.
 * Based on HL7 FHIR R4 MedicationRequest specification.
 * https://www.hl7.org/fhir/R4/medicationrequest.html
 */

// Citation source types for AI recommendations
export const citationSourceTypeSchema = z.enum([
  'transcript',
  'medication',
  'lab_result',
  'condition',
  'allergy',
  'clinical_note',
]);

// Citation schema
export const recommendationCitationSchema = z.object({
  id: z.string(),
  sourceType: citationSourceTypeSchema,
  sourceId: z.string().describe('Reference to source document'),
  text: z.string().describe('Excerpt or summary'),
  confidence: z.number().min(0).max(1).optional(),
});

// Medication schema
export const fhirMedicationSchema = z.object({
  name: z.string().describe('Name of the medication'),
  code: z.string().optional().describe('RxNorm, NDC, or other medication code'),
  form: z
    .string()
    .optional()
    .describe(
      'Form: tablet, capsule, syrup, injection, cream, ointment, gel, patch, inhaler, drops, powder, suppository, lozenge, etc.',
    ),
  strength: z
    .string()
    .optional()
    .describe('Strength with unit: 500mg, 10mg, 5mg/5mL, etc.'),
});

// Dosage schema
export const fhirDosageSchema = z.object({
  dose: z
    .string()
    .describe('Dose amount with unit, e.g., "500 mg", "10 mL", "1 tablet"'),
  frequency: z
    .string()
    .describe(
      'Dosing frequency: once daily, twice daily, three times daily, four times daily, every 4 hours, every 6 hours, every 8 hours, every 12 hours, as needed, at bedtime',
    ),
  route: z
    .string()
    .describe(
      'Administration route: oral, topical, subcutaneous, intramuscular, intravenous, inhalation, ophthalmic, otic, nasal, rectal, transdermal, sublingual',
    ),
  duration: z
    .string()
    .optional()
    .describe('Duration of treatment, e.g., "7 days", "30 days"'),
});

// Conflict severity
export const conflictSeveritySchema = z.enum(['severe', 'moderate', 'mild']);

// Full medication recommendation schema (matches FHIRMedicationRecommendation)
export const fhirMedicationRecommendationSchema = z.object({
  id: z.string().describe('Unique identifier for this recommendation'),
  medication: fhirMedicationSchema,
  suggestedDosage: fhirDosageSchema.optional(),
  reasonCode: z
    .string()
    .optional()
    .describe('Clinical indication for prescribing this medication'),
  citations: z
    .array(recommendationCitationSchema)
    .default([])
    .describe('Source citations - leave empty'),
  hasConflicts: z
    .boolean()
    .optional()
    .describe(
      'True if this medication conflicts with another recommendation in the list',
    ),
  conflictDescription: z
    .string()
    .optional()
    .describe(
      'Description of conflict, e.g., "May interact with Medication X also recommended"',
    ),
  conflictSeverity: conflictSeveritySchema.optional(),
});

// Array of recommendations (for LLM output)
export const medicineRecommendationSchema = z.array(
  fhirMedicationRecommendationSchema,
);

// Export inferred types
export type CitationSourceType = z.infer<typeof citationSourceTypeSchema>;
export type RecommendationCitation = z.infer<
  typeof recommendationCitationSchema
>;
export type FHIRMedication = z.infer<typeof fhirMedicationSchema>;
export type FHIRDosage = z.infer<typeof fhirDosageSchema>;
export type ConflictSeverity = z.infer<typeof conflictSeveritySchema>;
export type FHIRMedicationRecommendation = z.infer<
  typeof fhirMedicationRecommendationSchema
>;
export type MedicineOutputType = z.infer<typeof medicineRecommendationSchema>;
